import React, { useState, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

import SearchableInput from 'components/common/SearchableInput';
import { adminShelfApi, shelfApi } from 'api';
import { toastApiError } from 'utils/apiErrors';

const TransferShelfModal = ({ show, onHide, onSuccess, sourceShelf }) => {
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [shelfSearchValue, setShelfSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedShelf(null);
    setShelfSearchValue('');
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  const handleShelfSelect = useCallback(
    item => {
      if (item.id === sourceShelf?.id) {
        toast.warning('Cannot transfer to the same shelf');
        return;
      }
      setSelectedShelf(item);
      setShelfSearchValue(item.code);
    },
    [sourceShelf?.id],
  );

  const handleShelfInputChange = useCallback(
    term => {
      setShelfSearchValue(term);
      if (selectedShelf && term !== selectedShelf.code) {
        setSelectedShelf(null);
      }
    },
    [selectedShelf],
  );

  const handleTransfer = async () => {
    if (!selectedShelf) return;

    setIsSubmitting(true);
    try {
      const response = await adminShelfApi.transfer({
        source_shelf_id: sourceShelf.id,
        destination_shelf_id: selectedShelf.id,
      });

      toast.success(
        `Successfully transferred ${response.transferred_batch_count} batch(es)`,
      );
      closeModal();
      onSuccess();
    } catch (error) {
      toastApiError(error, 'Failed to transfer inventory');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : closeModal}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Transfer Inventory</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning">
          <Alert.Heading className="fs-9 mb-1">Warning</Alert.Heading>
          This action will move <strong>all inventory</strong> from the source
          shelf to the destination shelf. This action is{' '}
          <strong>irreversible</strong>.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Source Shelf</Form.Label>
          <Form.Control
            type="text"
            value={
              sourceShelf ? `${sourceShelf.name} (${sourceShelf.code})` : ''
            }
            disabled
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>
            Destination Shelf<span className="text-danger">*</span>
          </Form.Label>
          <SearchableInput
            apiService={shelfApi}
            searchField="code__icontains"
            additionalParams={{ active: 'true' }}
            displayField="code"
            value={shelfSearchValue}
            onChange={handleShelfInputChange}
            onSelect={handleShelfSelect}
            placeholder="Enter 2 or more characters to search shelf..."
            renderItem={item => (
              <div className="font-medium">
                {item?.name} ({item?.code})
              </div>
            )}
            visibleItems={5}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleTransfer}
          disabled={!selectedShelf || isSubmitting}
        >
          {isSubmitting ? 'Transferring...' : 'Transfer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferShelfModal;
